import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ArticleBulletList,
  ArticleParagraph,
  ArticleSection,
  ArticleSubsection,
  useSupportArticleStyles,
} from './article-shared';

export const PrescribingPolicyArticle: React.FC = () => {
  const { classes } = useSupportArticleStyles();

  return (
    <>
      <ArticleSection title="Policy Details">
        <ArticleParagraph>
          UpLift Health Technologies, Inc., UpLift Behavioral Health, P.C., UpLift Behavioral Health NJ, P.C., Shine
          Medical Providers, P.C., Shine Medical of CA, P.C., Shine Medical of NJ, P.C., and Shine Medical, P.C., and
          BetterHelp, delivering care through the BetterHelp Telehealth Platforms
        </ArticleParagraph>
        <Box className={classes.metaRow}>
          <Typography variant="body2"><strong>Policy Number:</strong> QA-019</Typography>
          <Typography variant="body2"><strong>Approval Date:</strong> 10/16/25</Typography>
          <Typography variant="body2"><strong>Approved By:</strong> Amar Mukhtar, DO - Associate Medical Director</Typography>
        </Box>
        <ArticleParagraph>
          The Program is reviewed at least annually by the UpLift Health Technologies, Inc. Quality Assurance (QA)
          Program and updated as necessary.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="1. Purpose">
        <ArticleParagraph>
          This policy outlines the prescribing practices for psychiatric care delivered via telehealth, ensuring safe,
          ethical, and evidence-based treatment while explicitly excluding the prescription of controlled substances.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="2. Scope">
        <ArticleParagraph>
          Applies to all licensed psychiatric providers (MDs, DOs, NPs, PAs) employed or contracted by Uplift Health
          Technologies, Inc., Uplift Behavioral Health, P.C., Uplift Behavioral Health NJ, P.C., Shine Medical
          Providers, P.C., Shine Medical of CA, P.C., Shine Medical of NJ, P.C., and Shine Medical, P.C., and
          BetterHelp, delivering care through the BetterHelp telehealth platforms.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="3. Policy Statement">
        <ArticleBulletList
          items={[
            'Providers may prescribe non-controlled psychiatric medications following a comprehensive virtual evaluation.',
            'Controlled substances (e.g., benzodiazepines, stimulants, opioids) are not prescribed under any circumstances via this platform.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="4. Prescribing Criteria">
        <ArticleParagraph>Prescriptions may be issued only when:</ArticleParagraph>
        <ArticleBulletList
          items={[
            'A valid patient-provider relationship has been established through synchronous video consultation, including verification of patient identity and location.',
            'A thorough psychiatric assessment has been conducted, including history, mental status exam, and review of prior treatments.',
            'The provider determines that the prescription is clinically indicated and safe, and is part of the BetterHelp approved formulary.',
            'The patient has provided informed consent for treatment and understands the risks, benefits, and alternatives.',
          ]}
        />
        <ArticleParagraph>
          Prescriptions, including dosage, must be documented in the patient&apos;s chart.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="5. Pharmacy Coordination">
        <ArticleParagraph>
          Prescriptions may only be sent electronically to the patient&apos;s preferred pharmacy using the secure
          e-prescribing system, TreatRX. Providers may only submit prescriptions by phone in the event that the
          e-prescribing system is experiencing an outage.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="6. Safety & Monitoring">
        <ArticleBulletList
          items={[
            'Providers must assess for potential drug interactions, contraindications, and comorbid conditions.',
            'Patients are encouraged to maintain care with a primary care provider for physical health monitoring.',
            'Adverse events or medication concerns must be documented and addressed promptly.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="7. Prohibited Practices">
        <ArticleSubsection title="Controlled substances">
          <ArticleParagraph>
            Providers are prohibited from prescribing any controlled substances, including but not limited to:
          </ArticleParagraph>
          <ArticleBulletList
            items={[
              'Stimulants (e.g., Adderall, Ritalin)',
              'Benzodiazepines (e.g., Xanax, Ativan)',
              'Narcotics or opioids',
            ]}
          />
        </ArticleSubsection>
        <ArticleSubsection title="Consultation requirements">
          <ArticleParagraph>
            Prescriptions may only be issued after a live video consultation with the following exceptions:
          </ArticleParagraph>
          <ArticleBulletList
            items={[
              'In the event of a technical failure, a visit may be conducted via phone and a prescription may be submitted if clinically appropriate; or',
              'In the event of a crisis event, a visit may be conducted via phone and a prescription may be submitted if clinically appropriate.',
            ]}
          />
          <ArticleParagraph>
            No prescribing based solely on questionnaire responses or asynchronous communication.
          </ArticleParagraph>
        </ArticleSubsection>
      </ArticleSection>

      <ArticleSection title="8. Compliance">
        <ArticleParagraph>All prescribing practices must comply with:</ArticleParagraph>
        <ArticleBulletList
          items={[
            'State medical board regulations',
            'DEA guidelines (as applicable)',
            'Telehealth-specific prescribing laws',
            'HIPAA and data privacy standards',
            'BetterHelp’s formulary and policies',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="9. Review & Updates">
        <ArticleParagraph>
          This policy is reviewed annually or as needed to reflect changes in clinical guidelines, legal requirements,
          or organizational practices.
        </ArticleParagraph>
      </ArticleSection>
    </>
  );
};
